import React from "react";
import type { InScopeOrphan } from "../../../models";
import "./DeleteImpactModal.css";

/** One dependency tie that dies if the deletion goes ahead. */
export interface DeleteImpactTie {
  /** The surviving endpoint's display label. */
  otherLabel: string;
  otherTaskCode: string;
  /** Where the survivor lives: its section name, "finished work", or "" for
   *  this section. */
  origin: string;
  /** "pred": the survivor feeds the deleted activity; "succ": the deleted
   *  activity feeds the survivor. */
  direction: "pred" | "succ";
  /** "FS", "SS+2d", … */
  rel: string;
  /** The survivor is OUTSIDE this section's plan (another section, or finished
   *  work) — severing it can shift dates across the schedule. */
  outside: boolean;
}

interface Props {
  /** Display names of the activities being deleted. */
  names: string[];
  ties: DeleteImpactTie[];
  /** Scope-leaf members not planned into this section whose ties to the
   *  deleted work would be severed. Absent/empty hides the block. */
  inScopeOrphans?: InScopeOrphan[];
  onConfirm: () => void;
  onCancel: () => void;
}

const ORPHAN_STATE_LABEL: Record<InScopeOrphan["state"], string> = {
  kept: "Left out of plan",
  unresolved: "Undecided",
};

/** The guard in front of every activity delete. Deleting work that's tied to
 *  OTHER sections silently rewires the building's logic — so those ties are
 *  spelled out and the user must explicitly acknowledge them. The same goes
 *  for ties to work left behind in the pulled WBS nodes. */
const DeleteImpactModal: React.FC<Props> = ({
  names,
  ties,
  inScopeOrphans,
  onConfirm,
  onCancel,
}) => {
  const outside = ties.filter((t) => t.outside);
  const inside = ties.filter((t) => !t.outside);
  const orphans = inScopeOrphans ?? [];
  const [orphansAcknowledged, setOrphansAcknowledged] = React.useState(false);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const n = names.length;
  // Outside ties are spelled out below but no longer gated behind a checkbox —
  // only the left-behind-work hand-off still needs an explicit acknowledgement.
  const canConfirm = orphans.length === 0 || orphansAcknowledged;

  return (
    <div className="delete-impact-overlay" onClick={onCancel}>
      <div className="delete-impact-modal" onClick={(e) => e.stopPropagation()}>
        <div className="delete-impact-title">
          Delete {n} {n === 1 ? "activity" : "activities"}?
        </div>
        <div className="delete-impact-names">
          {names.slice(0, 6).join(" · ")}
          {n > 6 ? ` · +${n - 6} more` : ""}
        </div>

        {outside.length > 0 && (
          <div className="delete-impact-block delete-impact-block--danger">
            <div className="delete-impact-block-title">
              ⚠ {outside.length} {outside.length === 1 ? "tie" : "ties"} to work
              OUTSIDE this plan will be severed
            </div>
            <p className="delete-impact-block-sub">
              These connections keep this section in step with the rest of the
              schedule. Severing them can shift dates in other sections when the
              schedule recalculates.
            </p>
            <ul className="delete-impact-list">
              {outside.map((t, i) => (
                <li key={i} className="delete-impact-item">
                  <span className="delete-impact-arrow">
                    {t.direction === "pred" ? "←" : "→"}
                  </span>
                  <span className="delete-impact-item-name">{t.otherLabel}</span>
                  <span className="delete-impact-item-code">{t.otherTaskCode}</span>
                  <span className="delete-impact-item-origin">
                    {t.direction === "pred" ? "feeds this work" : "follows this work"}
                    {t.origin ? ` · ${t.origin}` : ""}
                  </span>
                  <span className="delete-impact-item-rel">{t.rel}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {orphans.length > 0 && (
          <div className="delete-impact-block delete-impact-block--warn">
            <div className="delete-impact-block-title">
              ⚠ Work left behind in these WBS nodes loses ties
            </div>
            <p className="delete-impact-block-sub">
              This work stays in the schedule but isn't planned into this
              section yet. Deleting its tie partner severs the hand-off.
            </p>
            <ul className="delete-impact-list">
              {orphans.map((o, i) => (
                <li key={i} className="delete-impact-item">
                  <span className="delete-impact-item-name">{o.memberName}</span>
                  <span
                    className={`delete-impact-state-chip delete-impact-state-chip--${o.state}`}
                  >
                    {ORPHAN_STATE_LABEL[o.state]}
                  </span>
                  <span className="delete-impact-item-origin">
                    loses tie to {o.deletedName}
                  </span>
                  <span className="delete-impact-item-rel">{o.rel}</span>
                </li>
              ))}
            </ul>
            <label className="delete-impact-ack delete-impact-ack--warn">
              <input
                type="checkbox"
                checked={orphansAcknowledged}
                onChange={(e) => setOrphansAcknowledged(e.target.checked)}
              />
              I understand this left-behind work loses these ties on Publish.
            </label>
          </div>
        )}

        {inside.length > 0 && (
          <div className="delete-impact-block">
            <div className="delete-impact-block-title">
              {inside.length} {inside.length === 1 ? "tie" : "ties"} inside this
              plan {inside.length === 1 ? "goes" : "go"} away with{" "}
              {n === 1 ? "it" : "them"}
            </div>
          </div>
        )}

        {ties.length === 0 && orphans.length === 0 && (
          <div className="delete-impact-block">
            <div className="delete-impact-block-title delete-impact-block-title--safe">
              No dependency ties touch {n === 1 ? "this activity" : "these activities"}
              — nothing else moves.
            </div>
          </div>
        )}

        <p className="delete-impact-footnote">
          Existing activities are removed from the schedule when you Publish;
          freshly-added ones are just discarded. Undo (⌘Z) reverts this before
          then.
        </p>

        <div className="delete-impact-actions">
          <button type="button" className="delete-impact-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="delete-impact-btn delete-impact-btn--danger"
            disabled={!canConfirm}
            onClick={onConfirm}
          >
            Delete {n === 1 ? "activity" : `${n} activities`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteImpactModal;
